/**
 * Number of fixed label columns at the left of the crew grid before the day
 * cells begin: Job # | Project | PM | Type. Every colSpan over the label block
 * reads this, so adding or removing a label column can't leave a stale literal
 * behind and misalign the subtotal and Grand Total rows.
 *
 * ⚠️ It does NOT cover the per-row `<td>`s — a row writes its own cells, so a
 * new label column means a new `<td>` on the project row AND on the
 * fabrication/installation phase lines below it. Bumping this number alone
 * shifts the totals rows one column away from the data they total.
 */
export const LABEL_COLS = 4;

// Shared style constants for the crew-schedule grid + modals. Extracted
// verbatim from ManpowerPage.tsx.

/** Stepped green gradient: light → dark as headcount rises. */
export function crewFillStyle(count: number): React.CSSProperties {
  if (count <= 0) return {};
  if (count <= 2)  return { background: '#E6F2EB', color: '#015A28' };
  if (count <= 5)  return { background: '#C2DECD', color: '#015A28' };
  if (count <= 9)  return { background: '#8DC2A5', color: '#013A1A' };
  if (count <= 14) return { background: '#4FA478', color: '#FFFFFF' };
  if (count <= 20) return { background: '#027835', color: '#FFFFFF' };
  return                  { background: '#015A28', color: '#FFFFFF' };
}

export const TH: React.CSSProperties = {
  background: 'var(--ew-dark)',
  color: 'var(--ew-white)',
  fontWeight: 600,
  fontSize: 11,
  padding: '7px 4px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(255,255,255,0.07)',
};

export const TD: React.CSSProperties = {
  borderBottom: '1px solid var(--ew-n-100)',
  borderRight: '1px solid var(--ew-n-100)',
  textAlign: 'center',
  verticalAlign: 'middle',
};

export const NAV_BTN: React.CSSProperties = {
  padding: '5px 11px',
  border: '1px solid var(--ew-n-200)',
  borderRadius: 'var(--ew-r-md)',
  background: 'var(--ew-white)',
  cursor: 'pointer',
  fontSize: 15,
  color: 'var(--ew-dark)',
  fontFamily: 'var(--ew-font)',
  lineHeight: 1,
  fontWeight: 600,
};

export const TODAY_BTN: React.CSSProperties = {
  padding: '5px 13px',
  border: '1px solid var(--ew-green)',
  borderRadius: 'var(--ew-r-md)',
  background: 'var(--ew-white)',
  cursor: 'pointer',
  fontSize: 12,
  color: 'var(--ew-green)',
  fontFamily: 'var(--ew-font)',
  fontWeight: 700,
};

export const STATUS_CHIP: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 9px',
  fontSize: 11,
  fontWeight: 600,
  border: '1px solid',
  borderRadius: 'var(--ew-r-pill)',
  whiteSpace: 'nowrap',
};

export const UNMATCHED_BADGE: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 5,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#92400e',
  background: '#fef3c7',
  border: '1px solid #fde68a',
  borderRadius: 'var(--ew-r-sm)',
  padding: '1px 3px',
  verticalAlign: 'middle',
};

export const SYSTAC_BADGE: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 5,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--ew-performance)',
  background: '#fff7ed',
  border: '1px solid #fed7aa',
  borderRadius: 'var(--ew-r-sm)',
  padding: '1px 3px',
  verticalAlign: 'middle',
};

// Chip shown on rows in the synthetic "Needs Scheduling" section — a project
// is in that bucket when its currentStartDate falls in the visible window
// but no crew has been booked for it yet.
export const NEEDS_SCHEDULING_BADGE: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 5,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#92400e',
  background: '#fef3c7',
  border: '1px solid #fcd34d',
  borderRadius: 'var(--ew-r-sm)',
  padding: '1px 4px',
  verticalAlign: 'middle',
};

// Chip on a row whose first booked crew day disagrees with the project's planned
// start (#385). Rose rather than the amber "Needs Scheduling" wears, because the
// two mean different things: that one is work nobody has booked, this one is two
// people booking to different dates.
//
// ⚠️ THEY CAN CO-OCCUR, and an earlier version of this comment claimed they
// could not. "Needs Scheduling" reads the WINDOWED `dailyEntries` — no crew in
// the weeks on screen — while `firstCrewDate` is unbounded. A job whose planned
// start lands in the window with its only booking months later satisfies both,
// and that pair is informative rather than contradictory: nothing this week, and
// the one booking is in November. The Needs Scheduling tooltip says "in these
// weeks" for exactly that reason, and names the real first day when both apply.
// The amber section styling wins the row; both badges render.
export const START_MISMATCH_BADGE: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 5,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#9f1239',
  background: '#ffe4e6',
  border: '1px solid #fda4af',
  borderRadius: 'var(--ew-r-sm)',
  padding: '1px 4px',
  verticalAlign: 'middle',
  cursor: 'help',
};

/** Row tint and left accent for the same flag. */
export const START_MISMATCH_ROW_BG = '#fff1f2';
export const START_MISMATCH_ROW_ACCENT = '#e11d48';

export const MAINTENANCE_BADGE: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 5,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#1e40af',
  background: '#dbeafe',
  border: '1px solid #93c5fd',
  borderRadius: 'var(--ew-r-sm)',
  padding: '1px 4px',
  verticalAlign: 'middle',
};

export const LOAN_BADGE: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: 5,
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: '#5b21b6',
  background: '#ede9fe',
  border: '1px solid #c4b5fd',
  borderRadius: 'var(--ew-r-sm)',
  padding: '1px 4px',
  verticalAlign: 'middle',
};
